import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';

// While Google hasn't finished verifying MiPisto's Gmail access, the OAuth
// app runs "in production, unverified": anyone we enable can connect, but
// Google first shows a scary "Google hasn't verified this app" screen. This
// explains it right before we send the user there, so they don't bail.
//
// Usage at every "Conectar Gmail" entry point:
//   const went = await redirectToGoogleConsent(await api.connectGoogle());
//   if (!went) setBusy(false);   // user tapped "Ahora no"
// Mount <GoogleConsentNotice /> once (App.js). Once the backend reports
// app_verified (GOOGLE_OAUTH_VERIFIED=true), the notice is skipped entirely.

let showNotice = null;

export function redirectToGoogleConsent({ auth_url, app_verified }) {
  if (app_verified || !showNotice) {
    window.location.href = auth_url;
    return Promise.resolve(true);
  }
  return new Promise((resolve) => showNotice({ url: auth_url, resolve }));
}

export default function GoogleConsentNotice() {
  const [pending, setPending] = useState(null);

  useEffect(() => {
    showNotice = setPending;
    return () => { showNotice = null; };
  }, []);

  if (!pending) return null;

  const cancel = () => { pending.resolve(false); setPending(null); };
  const proceed = () => { pending.resolve(true); window.location.href = pending.url; };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/85"
      role="dialog"
      aria-modal="true"
      aria-labelledby="google-notice-title"
      data-testid="google-consent-notice"
    >
      <div className="w-full max-w-sm rounded-2xl bg-slate-900 border border-white/10 p-6 text-slate-200">
        <div className="w-11 h-11 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4">
          <ShieldAlert className="w-5 h-5 text-amber-400" strokeWidth={1.75} />
        </div>
        <h2 id="google-notice-title" className="text-lg font-semibold text-white">Antes de conectar tu Gmail</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Google está revisando la conexión de MiPisto con Gmail. Mientras termina, al continuar verás una pantalla de Google que dice{' '}
          <span className="text-slate-200 font-medium">"Google no verificó esta app"</span>.
        </p>
        <p className="text-sm text-slate-400 mt-3">Si decides continuar:</p>
        <ol className="mt-2 space-y-2 text-sm">
          {[
            <>Toca <span className="text-white font-medium">Configuración avanzada</span> (o <i>Advanced</i>).</>,
            <>Toca <span className="text-white font-medium">Ir a mipisto.net</span>.</>,
            <>Revisa el permiso — solo lectura — y toca <span className="text-white font-medium">Continuar</span>.</>,
          ].map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="w-5 h-5 shrink-0 rounded-full bg-violet-500/15 text-violet-300 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <span className="text-slate-300">{step}</span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed">
          MiPisto solo lee los correos de notificaciones de tu banco para registrar tus movimientos, y puedes desconectarlo cuando quieras. Si prefieres esperar a que Google termine su revisión, puedes conectarlo más adelante.
        </p>
        <div className="flex flex-col gap-2 mt-6">
          <button
            type="button"
            onClick={proceed}
            data-testid="google-consent-continue"
            className="h-11 rounded-full bg-violet-500 hover:bg-violet-600 text-on-brand text-sm font-semibold transition-colors active:scale-[0.98]"
          >
            Continuar a Google
          </button>
          <button type="button" onClick={cancel} className="hit-44 h-10 text-sm text-slate-400 hover:text-white">
            Prefiero esperar
          </button>
        </div>
      </div>
    </div>
  );
}
